import React, { memo, useEffect, useState } from 'react';
import MediumTypography from '../../../components/formlib/MediumTypography';
import { Box } from '@mui/system';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import ButtonComponent from '../../../components/formlib/ButtonComponent';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { ReactComponent as AcceptIcon } from '../../../assets/images/acceptIcon.svg';
import { ReactComponent as WarningIcon } from '../../../assets/images/WarningIcon.svg';
import { ReactComponent as RemainingTask } from '../../../assets/images/remainingTask.svg';
import { ReactComponent as Info } from '../../../assets/images/infoIcon.svg';
import { ReactComponent as Checklist } from '../../../assets/images/checklist.svg';
import CheckBoxComponent from '../../../components/formlib/CheckBoxComponent';
import { updateTBRPreRequisite } from '../../../redux/features/ifsp/ifspTBRPreRequisiteSlice';
import ActionToolTip from '../../../components/formlib/ActionToolTip';

type TasksDescriptionType = {
  id?: string;
  labelId: string;
  label: string;
  completed: boolean;
};

export type UploadToTBRPayloadType = {
  isCommonAreaChecked: boolean;
  isOutcomeChecked: boolean;
  isServiceDeliveryChecked: boolean;
  isTransitionChecked: boolean;
};

const initialTasks: TasksDescriptionType[] = [
  {
    id: 'COMMON_SECTION',
    labelId: 'IFSP.TBRCheck.TaskOne',
    label:
      'Complete IFSP first Page (Meeting Date, Signed Date, Primary Setting Code)',
    completed: false,
  },
  {
    id: 'OUTCOME_SECTION',
    labelId: 'IFSP.TBRCheck.TaskTwo',
    label: 'Outcomes (At least one Outcome needs to be added)',
    completed: false,
  },
  {
    id: 'SERVICE_DELIVERY_SECTION',
    labelId: 'IFSP.TBRCheck.TaskThree',
    label:
      'Service Delivery (At least one service needs to be added and Locked)',
    completed: false,
  },
];
type IfspTBRCheckModalType = {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
};

const IfspTBRCheckModal = memo(
  ({ open, onClose, onSave }: IfspTBRCheckModalType) => {
    const ifspTBRPreRequisites = useAppSelector(
      (state) => state.ifspTBRPreRequisite,
    );
    const ifspPermission = useAppSelector(
      (state) => state.ifspPermissionDetails,
    );
    const dispatch = useAppDispatch();
    const [tasks, setTasks] = useState<TasksDescriptionType[]>(initialTasks);

    const isChanged =
      ifspTBRPreRequisites.isCommonAreaChecked !==
        ifspTBRPreRequisites.isAlreadyCommonAreaChecked ||
      ifspTBRPreRequisites.isOutcomeChecked !==
        ifspTBRPreRequisites.isAlreadyOutcomeChecked ||
      ifspTBRPreRequisites.isServiceDeliveryChecked !==
        ifspTBRPreRequisites.isAlreadyServiceDeliveryChecked ||
      ifspTBRPreRequisites.isTransitionChecked !==
        ifspTBRPreRequisites.isAlreadyTransitionChecked;

    const nothingSelected =
      !ifspTBRPreRequisites.isCommonAreaChecked &&
      !ifspTBRPreRequisites.isOutcomeChecked &&
      !ifspTBRPreRequisites.isServiceDeliveryChecked &&
      !ifspTBRPreRequisites.isTransitionChecked;

    const getTaskCompletion = (index: number) => {
      switch (index) {
        case 0:
          return ifspTBRPreRequisites.commonAreaReadyToUpload;
        case 1:
          return ifspTBRPreRequisites.outcomeReadyToUpload;
        case 2:
          return ifspTBRPreRequisites.serviceDeliveryReadyToUpload;
        default:
          return false;
      }
    };

    useEffect(() => {
      setTasks((prevTasks) =>
        prevTasks.map((task, index) => ({
          ...task,
          completed: getTaskCompletion(index),
        })),
      );
    }, [ifspTBRPreRequisites]);

    const handleInitialCheckConditions = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      dispatch(
        updateTBRPreRequisite({
          isCommonAreaChecked: !ifspTBRPreRequisites.isCommonAreaChecked,
          isOutcomeChecked: !ifspTBRPreRequisites.isOutcomeChecked,
          isServiceDeliveryChecked:
            !ifspTBRPreRequisites.isServiceDeliveryChecked,
          isTransitionChecked: event.target.checked
            ? ifspTBRPreRequisites.isTransitionChecked
            : false,
        }),
      );
    };
    return (
      <Dialog open={open} fullWidth maxWidth={'md'}>
        <DialogTitle>
          <MediumTypography
            labelid={'IFSP.TBRCheck.Title'}
            defaultlabel={'Pre-Upload Requirements'}
            fontweight={700}
            fontSize="24px"
            textColor="#2a4241"
            lineHeight="24px"
          />
        </DialogTitle>
        <DialogContent sx={{ paddingBottom: '0px' }}>
          {/* Checklist */}
          {(ifspTBRPreRequisites.tbrIfspId > 0 ||
            (ifspTBRPreRequisites.tbrIfspId <= 0 &&
              tasks[0].completed &&
              tasks[1].completed &&
              tasks[2].completed)) &&
            ifspPermission.hasUploadToTBRPermission &&
            ifspPermission.hasClientEditPermission && (
              <>
                <Box className="flex__ align__items__center">
                  <Checklist />
                  <MediumTypography
                    labelid="IFSP.TBRCheck.RemainingTasksChecklist"
                    defaultlabel="Checklist"
                    fontweight={700}
                    fontSize="12px"
                    lineHeight="16px"
                    textColor="#2a4241"
                    className="pl-xs"
                  />
                </Box>
                <Box
                  sx={{
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    borderColor: '#CFDEDE',
                    borderRadius: '4px',
                    padding: '12px',
                  }}
                  className="mt-sm mb-xlg"
                >
                  <Box className="flex__ align__items__center">
                    <CheckBoxComponent
                      ischecked={ifspTBRPreRequisites.isCommonAreaChecked}
                      CheckHandleChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        if (ifspTBRPreRequisites.tbrIfspId <= 0) {
                          handleInitialCheckConditions(event);
                        } else {
                          dispatch(
                            updateTBRPreRequisite({
                              isCommonAreaChecked:
                                !ifspTBRPreRequisites.isCommonAreaChecked,
                            }),
                          );
                        }
                      }}
                      defaultlabelid={'IFSP First Page'}
                      labelid={'IFSP.TBRCheck.CommonArea'}
                      disabled={
                        ifspTBRPreRequisites.isAlreadyCommonAreaChecked ||
                        !ifspTBRPreRequisites.commonAreaReadyToUpload ||
                        ifspTBRPreRequisites.tbrIfspId > 0
                      }
                    />
                    <ActionToolTip
                      defaultLabel="IFSP Meeting Date, IFSP Signed Date, Primary Setting Code, Justification"
                      labelId="IFSP.TBRCheck.CommonAreaTooltip"
                      label="IFSP Meeting Date, IFSP Signed Date, Primary Setting Code, Justification"
                      placement="top"
                    >
                      <Info
                        style={{
                          cursor: 'pointer',
                          marginLeft: '8px',
                        }}
                      />
                    </ActionToolTip>
                  </Box>
                  <Box className="flex__ align__items__center">
                    <CheckBoxComponent
                      ischecked={ifspTBRPreRequisites.isOutcomeChecked}
                      CheckHandleChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        if (ifspTBRPreRequisites.tbrIfspId <= 0) {
                          handleInitialCheckConditions(event);
                        } else {
                          dispatch(
                            updateTBRPreRequisite({
                              isOutcomeChecked:
                                !ifspTBRPreRequisites.isOutcomeChecked,
                            }),
                          );
                        }
                      }}
                      defaultlabelid={'Outcomes'}
                      labelid={'IFSP.TBRCheck.Outcome'}
                      disabled={
                        ifspTBRPreRequisites.isAlreadyOutcomeChecked ||
                        !ifspTBRPreRequisites.outcomeReadyToUpload
                      }
                    />
                    <ActionToolTip
                      defaultLabel="All the Outcomes added to the IFSP"
                      labelId="IFSP.TBRCheck.OutcomeTooltip"
                      label="All the Outcomes added to the IFSP"
                      placement="top"
                    >
                      <Info
                        style={{
                          cursor: 'pointer',
                          marginLeft: '8px',
                        }}
                      />
                    </ActionToolTip>
                  </Box>
                  <Box className="flex__ align__items__center">
                    <CheckBoxComponent
                      ischecked={ifspTBRPreRequisites.isServiceDeliveryChecked}
                      CheckHandleChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        if (ifspTBRPreRequisites.tbrIfspId <= 0) {
                          handleInitialCheckConditions(event);
                        } else {
                          dispatch(
                            updateTBRPreRequisite({
                              isServiceDeliveryChecked:
                                !ifspTBRPreRequisites.isServiceDeliveryChecked,
                            }),
                          );
                        }
                      }}
                      defaultlabelid={'Service Delivery Plan'}
                      labelid={'IFSP.TBRCheck.ServiceDelivery'}
                      disabled={
                        ifspTBRPreRequisites.isAlreadyServiceDeliveryChecked ||
                        !ifspTBRPreRequisites.serviceDeliveryReadyToUpload
                      }
                    />
                    <ActionToolTip
                      defaultLabel="All the Services added to the IFSP"
                      labelId="IFSP.TBRCheck.ServiceDeliveryTooltip"
                      label="All the Services added to the IFSP"
                      placement="top"
                    >
                      <Info
                        style={{
                          cursor: 'pointer',
                          marginLeft: '8px',
                        }}
                      />
                    </ActionToolTip>
                  </Box>
                  <Box className="flex__ align__items__center">
                    <CheckBoxComponent
                      ischecked={ifspTBRPreRequisites.isTransitionChecked}
                      CheckHandleChange={() => {
                        dispatch(
                          updateTBRPreRequisite({
                            isTransitionChecked:
                              !ifspTBRPreRequisites.isTransitionChecked,
                          }),
                        );
                      }}
                      defaultlabelid={'Transition Plan'}
                      labelid={'IFSP.TBRCheck.Transition'}
                      disabled={
                        (ifspTBRPreRequisites.tbrIfspId <= 0 &&
                          (!ifspTBRPreRequisites.isCommonAreaChecked ||
                            !ifspTBRPreRequisites.isOutcomeChecked ||
                            !ifspTBRPreRequisites.isServiceDeliveryChecked)) ||
                        !ifspTBRPreRequisites.transitionReadyToUpload ||
                        ifspTBRPreRequisites.isAlreadyTransitionChecked
                      }
                    />
                    <ActionToolTip
                      defaultLabel="All the Transition data added to the IFSP"
                      labelId="IFSP.TBRCheck.TransitionTooltip"
                      label="All the Transition data added to the IFSP"
                      placement="top"
                    >
                      <Info
                        style={{
                          cursor: 'pointer',
                          marginLeft: '8px',
                        }}
                      />
                    </ActionToolTip>
                  </Box>
                </Box>
              </>
            )}

          {/*  Remaining Tasks */}
          {ifspTBRPreRequisites.tbrIfspId <= 0 &&
            tasks.some((task) => !task.completed) && (
              <>
                <Box className="flex__ align__items__center">
                  <RemainingTask />
                  <MediumTypography
                    labelid="IFSP.TBRCheck.RemainingTasks"
                    defaultlabel="Remaining Tasks"
                    fontweight={700}
                    fontSize="12px"
                    lineHeight="16px"
                    textColor="#2a4241"
                    className="pl-xs"
                  />
                </Box>
                <Box
                  sx={{
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    borderColor: '#CFDEDE',
                    borderRadius: '4px',
                    padding: '12px',
                  }}
                  className="mt-sm mb-lg"
                >
                  {tasks
                    .filter((task) => !task.completed)
                    .map((task, index) => (
                      <Box
                        className="flex__ align__items__center pt-sm pb-sm"
                        key={task.labelId}
                      >
                        <MediumTypography
                          label={`${index + 1}.`}
                          fontweight={500}
                          textColor="#2a4241"
                          lineHeight="14px"
                        />
                        <MediumTypography
                          labelid={task.labelId}
                          defaultlabel={task.label}
                          fontweight={500}
                          textColor="#2a4241"
                          lineHeight="14px"
                          className="pl-xs"
                        />
                      </Box>
                    ))}
                </Box>
              </>
            )}

          {/* Completed Tasks */}
          {ifspTBRPreRequisites.tbrIfspId <= 0 &&
            tasks.some((task) => task.completed) && (
              <>
                <Box className="flex__ align__items__center">
                  <AcceptIcon />
                  <MediumTypography
                    labelid="IFSP.TBRCheck.CompletedTasks"
                    defaultlabel="Completed Tasks"
                    fontweight={700}
                    fontSize="12px"
                    lineHeight="16px"
                    textColor="#2a4241"
                    className="ml-xs"
                  />
                </Box>
                <Box
                  sx={{
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    borderColor: '#CFDEDE',
                    borderRadius: '4px',
                    padding: '12px',
                  }}
                  className="mt-sm mb-lg"
                >
                  {tasks
                    .filter((task) => task.completed)
                    .map((task, index) => (
                      <Box
                        className="flex__ align__items__center pt-sm pb-sm"
                        key={task.labelId}
                      >
                        <MediumTypography
                          label={`${index + 1}.`}
                          fontweight={500}
                          textColor="#2a4241"
                          lineHeight="14px"
                        />
                        <MediumTypography
                          labelid={task.labelId}
                          defaultlabel={task.label}
                          fontweight={500}
                          textColor="#2a4241"
                          lineHeight="14px"
                          className="pl-xs"
                        />
                      </Box>
                    ))}
                </Box>
              </>
            )}

          {ifspTBRPreRequisites.tbrIfspId > 0 &&
            !ifspPermission.hasUploadToTBRPermission && (
              <>
                <Box className="flex__ align__items__center">
                  <AcceptIcon />
                  <MediumTypography
                    labelid="IFSP.TBRCheck.CompletedTasks"
                    defaultlabel="Completed Tasks"
                    fontweight={700}
                    fontSize="12px"
                    lineHeight="16px"
                    textColor="#2a4241"
                    className="ml-xs"
                  />
                </Box>
                <Box
                  sx={{
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    borderColor: '#CFDEDE',
                    borderRadius: '4px',
                    padding: '12px',
                  }}
                  className="mt-sm mb-lg"
                >
                  {tasks.map((task, index) => (
                    <Box
                      className="flex__ align__items__center pt-sm pb-sm"
                      key={task.labelId}
                    >
                      <MediumTypography
                        label={`${index + 1}.`}
                        fontweight={500}
                        textColor="#2a4241"
                        lineHeight="14px"
                      />
                      <MediumTypography
                        labelid={task.labelId}
                        defaultlabel={task.label}
                        fontweight={500}
                        textColor="#2a4241"
                        lineHeight="14px"
                        className="pl-xs"
                      />
                    </Box>
                  ))}
                </Box>
              </>
            )}
          {ifspTBRPreRequisites.pendingUploadEvaluationToTbr && (
            <Box className="flex__ align__items__center pl-md pb-sm">
              <MediumTypography
                labelid="IFSP.TBRCheck.PendingUploadEvaluationToTBR"
                defaultlabel={
                  '* An evaluation linked to this IFSP is pending upload to TBR'
                }
                className="errorText-md"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: '#ECF9F8',
            paddingTop: '12px',
            marginTop: '0px',
            justifyContent:
              !ifspPermission.hasUploadToTBRPermission ||
              !ifspPermission.hasClientEditPermission
                ? 'flex-start'
                : 'flex-end',
          }}
        >
          {(!ifspPermission.hasUploadToTBRPermission ||
            !ifspPermission.hasClientEditPermission) && (
            <Box className="flex__ align__items__center">
              <WarningIcon height={'12px'} width={'14px'} />
              <MediumTypography
                labelid="IFSP.TBRCheck.Warning"
                defaultlabel={
                  "You don't have permission to transfer the IFSP data to TBR. Reach to your Office Administrator"
                }
                fontweight={500}
                fontSize="12px"
                lineHeight="12px"
                textColor="#2a4241"
                className="ml-xs"
              />
              <ButtonComponent
                className="btn-primary btn-cancel ml-md"
                labelId={'cancelText'}
                defaultLabelId="Cancel"
                variantType="outlined"
                onClick={() => {
                  onClose();
                }}
              />
            </Box>
          )}
          {ifspPermission.hasUploadToTBRPermission &&
            ifspPermission.hasClientEditPermission && (
              <Box
                component="div"
                className="flex__ justifyContent-FlexEnd mr-md"
              >
                <ButtonComponent
                  className="btn-primary btn-cancel"
                  labelId={'cancelText'}
                  defaultLabelId="Cancel"
                  variantType="outlined"
                  onClick={() => {
                    onClose();
                  }}
                />
                <ButtonComponent
                  className="btn-primary btn-submit ml-md"
                  labelId="proceedText"
                  defaultLabelId="Proceed"
                  variantType="contained"
                  onClick={() => {
                    onSave();
                  }}
                  disabled={!isChanged || nothingSelected}
                />
              </Box>
            )}
        </DialogActions>
      </Dialog>
    );
  },
);

export default IfspTBRCheckModal;
